/* Dabi Deri — ortak arayüz
   1) Mıknatıslı düğmeler: [data-dabi-magnetic] imleci birkaç piksel izler (yalnız ince imleçte).
   2) Şerit gezinme okları: [data-dabi-strip-nav] taşıyan her şeride iki yanına buzlu cam ok ekler.
      - Dokun / tıkla: bir adım kaydırır (yumuşak).
      - Basılı tut: hız kademeli artar (1× → 2× → 4× → 8×), halka dolar, bırakınca süzülerek durur.
      - Sonsuz akan şeritte (CSS animasyonu) animasyonun zamanını ileri/geri sarar; akış bozulmaz.
      - Normal kaydırmalı şeritte scrollLeft ile çalışır; başta/sonda ilgili ok gizlenir.
   Şerit işaretleme:
      data-dabi-strip-nav            görünür alan (taşan/kayan kap)
      data-dabi-strip-track          (isteğe bağlı) hareket eden liste; yoksa ilk çocuk
      data-dabi-strip-look="dark slim"  görünüm (koyu zemin, ince şerit)
   Otomatik kayan şeritler 'dabi:strip:hold' olayını dinleyip basılı tutarken kendini durdurabilir.
   Oklar şeridin içinde değil, sayfa düzeyindeki tek katmanda (.dabi-snav-layer, z 49) durur: büyüyen önizleme,
   sepet rozeti ya da ürün rafı açılsa da üstte kalır ve her zaman tıklanır. Konum her karede şeritten okunur;
   şerit görünmüyorsa (kapalı panel, gizli raf, açılma animasyonu: .is-unrolling / [data-snav-wait]), başlığın
   altına düştüyse ya da başlık menüsü açıksa oklar gizlenir. */
(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1) Mıknatıs ---------- */
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  let magnetEl = null;
  const magnetReset = (el) => { el.style.removeProperty('--dabi-mx'); el.style.removeProperty('--dabi-my'); };
  document.addEventListener('pointermove', (e) => {
    if (!fine.matches || reduced()) return;
    const el = e.target.closest?.('[data-dabi-magnetic]');
    if (magnetEl && magnetEl !== el) { magnetReset(magnetEl); magnetEl = null; }
    if (!el) return;
    magnetEl = el;
    const r = el.getBoundingClientRect();
    const dx = ((e.clientX - r.left) / r.width - 0.5) * 8;
    const dy = ((e.clientY - r.top) / r.height - 0.5) * 6;
    el.style.setProperty('--dabi-mx', `${dx.toFixed(1)}px`);
    el.style.setProperty('--dabi-my', `${dy.toFixed(1)}px`);
  }, { passive: true });
  document.addEventListener('pointerleave', () => { if (magnetEl) { magnetReset(magnetEl); magnetEl = null; } }, true);

  /* ---------- 2) Şerit okları ---------- */
  const L = Object.assign({ prev: 'Sola kaydır', next: 'Sağa kaydır', hold: '' }, window.DabiStripLabels || {});
  const ICON = '<svg viewBox="0 0 24 24" focusable="false"><path d="M3.5 12h8.5"/><path d="M8.5 6.5 14 12l-5.5 5.5"/><path d="M14.5 6.5 20 12l-5.5 5.5" opacity=".42"/></svg>';
  const LEVELS = [[0.55, '1×'], [1.1, '2×'], [2.0, '4×'], [Infinity, '8×']];
  const HOLD_DELAY = 240; // ms: bundan uzun basış "basılı tutma"
  let uid = 0;

  const ease = (t) => 1 - (1 - t) ** 3;

  // sayfa düzeyindeki ok katmanı
  let layerEl = null;
  const layer = () => {
    if (layerEl && layerEl.isConnected) return layerEl;
    layerEl = document.createElement('div');
    layerEl.className = 'dabi-snav-layer';
    document.body.appendChild(layerEl);
    return layerEl;
  };
  // başlık: menü / arama açıkken oklar gizli; ok başlığın altına düşerse gizli
  const headerState = () => {
    const wrap = document.querySelector('.dabi-header-wrap') || document.querySelector('.shopify-section-header');
    const hr = wrap ? wrap.getBoundingClientRect() : null;
    const menuOpen = !!document.querySelector('.dabi-header details[open], .header details[open], body.overflow-hidden-mobile, body.overflow-hidden-tablet');
    return { bottom: hr && hr.bottom > 0 ? hr.bottom : 0, menuOpen };
  };
  let hdr = { bottom: 0, menuOpen: false };
  const strips = new Set();

  class Strip {
    constructor(vp) {
      this.vp = vp;
      this.track = vp.querySelector('[data-dabi-strip-track]') || vp.firstElementChild || vp;
      this.host = vp.parentElement;
      this.loop = vp.hasAttribute('data-dabi-strip-loop');
      if (!this.host) return;
      if (!vp.id) vp.id = `DabiStrip-${(uid += 1)}`;
      this.host.classList.add('dabi-snav-host');
      if (getComputedStyle(this.host).position === 'static') this.host.style.position = 'relative';

      const looks = (vp.dataset.dabiStripLook || '').split(/\s+/).filter(Boolean).map((l) => `dabi-snav--${l}`).join(' ');
      this.nav = document.createElement('div');
      this.nav.className = `dabi-snav ${looks}`.trim();
      this.nav.setAttribute('data-dabi-snav', '');
      // oklar sayfa katmanında; açılır ögeler imleç oktayken onu bu şeridin içinde saysın (DabiStrips.owner)
      this.nav.dabiOwner = vp;
      this.btns = {
        prev: this.makeBtn(-1),
        next: this.makeBtn(1),
      };
      this.nav.append(this.btns.prev, this.btns.next);
      layer().appendChild(this.nav);
      this.key = '';
      strips.add(this);

      this.place = this.place.bind(this);
      this.ro = new ResizeObserver(this.place);
      this.ro.observe(vp);
      this.ro.observe(this.host);
      vp.addEventListener('scroll', () => this.updateEnds(), { passive: true });
      // içerik sonradan dolan şeritler (ör. JS ile eklenen gün kutuları)
      this.mo = new MutationObserver(() => this.place());
      this.mo.observe(vp, { childList: true });
      // Oklar şeridin dışında (kardeş öğe); kendi kendine kayan şeritlerin
      // "üzerine gelince dur" mantığı oklarda da çalışsın diye olayları köprüle
      this.nav.addEventListener('pointerover', (e) => { if (!this.nav.contains(e.relatedTarget)) vp.dispatchEvent(new PointerEvent('pointerenter')); });
      this.nav.addEventListener('pointerout', (e) => { if (!this.nav.contains(e.relatedTarget) && !vp.contains(e.relatedTarget)) vp.dispatchEvent(new PointerEvent('pointerleave')); });
      window.addEventListener('resize', this.place, { passive: true });
      this.place();
    }

    makeBtn(dir) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `dabi-snav__btn dabi-snav__btn--${dir < 0 ? 'prev' : 'next'}`;
      const label = dir < 0 ? L.prev : L.next;
      b.setAttribute('aria-label', label);
      b.setAttribute('aria-controls', this.vp.id);
      if (L.hold) b.title = `${label} · ${L.hold}`;
      b.innerHTML = `<span class="dabi-snav__icon" aria-hidden="true">${ICON}${ICON}</span><svg class="dabi-snav__ring" viewBox="0 0 36 36" aria-hidden="true" focusable="false"><circle cx="18" cy="18" r="16.5" pathLength="100"/></svg><span class="dabi-snav__speed" aria-hidden="true">1×</span>`;

      let fromPointer = false;
      b.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        fromPointer = true;
        e.preventDefault();
        try { b.setPointerCapture(e.pointerId); } catch (err) { /* yok */ }
        this.press = { dir, b, held: false };
        this.holdTimer = window.setTimeout(() => { if (this.press) { this.press.held = true; this.beginHold(dir, b); } }, HOLD_DELAY);
      });
      const release = () => {
        if (!this.press) return;
        window.clearTimeout(this.holdTimer);
        const { held } = this.press;
        this.press = null;
        if (held) this.endHold(dir, b);
        else this.nudge(dir);
      };
      b.addEventListener('pointerup', release);
      b.addEventListener('pointercancel', release);
      b.addEventListener('lostpointercapture', release);
      b.addEventListener('contextmenu', (e) => e.preventDefault());
      b.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (fromPointer) { fromPointer = false; return; }
        this.nudge(dir); // klavye: Enter / Boşluk (basılı tutunca tekrar eder)
      });
      return b;
    }

    /* CSS ile akan liste ise o animasyon; yoksa null (kaydırma kipi) */
    anim() {
      const list = this.track.getAnimations ? this.track.getAnimations() : [];
      return list.find((a) => a.effect && Number(a.effect.getTiming().duration) > 0 && a.effect.getComputedTiming().iterations === Infinity) || null;
    }

    rtl() { return getComputedStyle(this.vp).direction === 'rtl'; }

    canScroll() { return this.vp.scrollWidth > this.vp.clientWidth + 4; }

    place() {
      if (!this.vp.isConnected) { this.destroy(); return; }
      const r = this.vp.getBoundingClientRect();
      // dikeyde: kartlı şeritlerde görselin ortası (data-dabi-strip-center="seçici"), yoksa şeridin ortası
      let top = r.top;
      let height = r.height;
      const sel = this.vp.dataset.dabiStripCenter;
      const ref = sel ? this.vp.querySelector(sel) : null;
      if (ref) {
        const rr = ref.getBoundingClientRect();
        if (rr.height > 0) { top = rr.top; height = rr.height; }
      }
      // yatayda: kenardan kenara (100vw) şeritlerde oklar görünen pencerede kalsın
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      // ekran kenarına dayanan şeritte oklar kenardaki sabit sekmelerle (ör. Sözümüz) çakışmasın
      const EDGE = 30;
      const left = r.left <= 4 ? EDGE : Math.max(r.left, 0);
      const right = r.right >= vw - 4 ? vw - EDGE : Math.min(r.right, vw);
      const mid = top + height / 2;
      let show = r.width > 0 && r.height > 0 && right - left > 40 && r.bottom > 0 && r.top < vh;
      if (show && (this.vp.closest('.is-unrolling, .is-rolling, [data-snav-wait]') || hdr.menuOpen || mid < hdr.bottom + 8 || mid > vh - 8)) show = false;
      if (show && this.vp.checkVisibility && !this.vp.checkVisibility({ opacityProperty: true, visibilityProperty: true })) show = false;
      // telefonda ürün rafı paneli açıkken yalnız rafın okları
      if (show && document.documentElement.classList.contains('is-dabi-sheet') && !this.vp.closest('.dabi-shelf, [data-dabi-sheet-host], .dabi-tabsheet')) show = false;
      const active = show && !!(this.anim() || this.canScroll());
      const key = active ? `${Math.round(top)}|${Math.round(left)}|${Math.round(right - left)}|${Math.round(height)}` : 'off';
      if (key !== this.key) {
        this.key = key;
        if (active) {
          const st = this.nav.style;
          st.setProperty('--snav-top', `${top}px`);
          st.setProperty('--snav-left', `${left}px`);
          st.setProperty('--snav-w', `${Math.max(0, right - left)}px`);
          st.setProperty('--snav-h', `${height}px`);
        }
        this.nav.hidden = !active;
      }
      if (active) this.updateEnds();
    }

    destroy() {
      strips.delete(this);
      if (this.ro) this.ro.disconnect();
      if (this.mo) this.mo.disconnect();
      this.nav.remove();
      if (this.vp.dabiStrip === this) delete this.vp.dabiStrip;
    }

    updateEnds() {
      const marquee = !!this.anim();
      let atStart = false;
      let atEnd = false;
      if (!marquee && !this.loop) {
        const max = this.vp.scrollWidth - this.vp.clientWidth;
        const x = Math.abs(this.vp.scrollLeft);
        atStart = x <= 2;
        atEnd = x >= max - 2;
      }
      this.btns.prev.classList.toggle('is-off', atStart);
      this.btns.next.classList.toggle('is-off', atEnd);
    }

    /* px > 0: ileri (içerik sola akar, sağdakiler görünür) */
    move(px) {
      const a = this.anim();
      if (a) {
        const t = a.effect.getTiming();
        const dur = Number(t.duration);
        const dist = (this.track.scrollWidth || this.track.getBoundingClientRect().width) / 2;
        if (!dur || !dist) return;
        const sign = String(t.direction || '').includes('reverse') ? -1 : 1;
        let ct = (Number(a.currentTime) || 0) + (px / (dist / dur)) * sign * (this.rtl() ? -1 : 1);
        if (ct < 0) ct = (ct % dur) + dur;
        a.currentTime = ct;
      } else {
        this.vp.scrollLeft += px * (this.rtl() ? -1 : 1);
        this.wrap();
      }
    }

    /* İki kez basılmış (sonsuz) şeritte yarıya gelince görünmez biçimde başa/sona sar */
    wrapAt() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return (this.track.scrollWidth + gap) / 2;
    }

    wrap(ahead = 0) {
      if (!this.loop || this.rtl()) return;
      const w = this.wrapAt();
      if (!(w > 0)) return;
      const x = this.vp.scrollLeft + ahead;
      if (x >= w) this.vp.scrollLeft -= w;
      else if (x <= 0) this.vp.scrollLeft += w;
    }

    nudge(dir) {
      if (this.holding) return;
      const a = this.anim();
      const dist = Math.max(160, this.vp.clientWidth * (a ? 0.45 : 0.8));
      if (!a) {
        this.wrap(dir * dist);
        this.vp.scrollBy({ left: dir * dist * (this.rtl() ? -1 : 1), behavior: reduced() ? 'auto' : 'smooth' });
        return;
      }
      if (reduced()) { this.move(dir * dist); return; }
      const T = 520;
      const t0 = performance.now();
      let done = 0;
      window.cancelAnimationFrame(this.nudgeRaf);
      const step = (now) => {
        const p = Math.min(1, (now - t0) / T);
        const want = ease(p) * dist;
        this.move(dir * (want - done));
        done = want;
        if (p < 1) this.nudgeRaf = window.requestAnimationFrame(step);
      };
      this.nudgeRaf = window.requestAnimationFrame(step);
    }

    beginHold(dir, b) {
      this.holding = true;
      this.nav.classList.add('is-holding');
      b.classList.add('is-holding');
      this.vp.dispatchEvent(new CustomEvent('dabi:strip:hold', { bubbles: true, detail: { on: true } }));
      if (!this.anim()) { this.vp.style.scrollSnapType = 'none'; this.vp.style.scrollBehavior = 'auto'; }
      const badge = b.querySelector('.dabi-snav__speed');
      const t0 = performance.now();
      let last = t0;
      let level = '';
      const step = (now) => {
        if (!this.holding) return;
        const dt = Math.min(48, now - last);
        last = now;
        const p = Math.min(1, (now - t0) / 1800);
        const e = p * p * (3 - 2 * p);
        const v = 0.32 + e * 2.7; // px / ms
        this.vel = v;
        b.style.setProperty('--p', e.toFixed(3));
        const lv = LEVELS.find(([lim]) => v < lim)[1];
        if (lv !== level) {
          level = lv;
          badge.textContent = lv;
          if (lv !== '1×' && navigator.vibrate) { try { navigator.vibrate(8); } catch (err) { /* yok */ } }
        }
        this.move(dir * v * dt);
        this.updateEnds();
        if (!this.anim() && b.classList.contains('is-off')) { this.endHold(dir, b); return; }
        this.holdRaf = window.requestAnimationFrame(step);
      };
      this.holdRaf = window.requestAnimationFrame(step);
    }

    endHold(dir, b) {
      if (!this.holding) return;
      this.holding = false;
      window.cancelAnimationFrame(this.holdRaf);
      this.nav.classList.remove('is-holding');
      b.classList.remove('is-holding');
      b.style.setProperty('--p', '0');
      const finish = () => {
        this.vp.style.scrollSnapType = '';
        this.vp.style.scrollBehavior = '';
        this.updateEnds();
        this.vp.dispatchEvent(new CustomEvent('dabi:strip:hold', { bubbles: true, detail: { on: false } }));
      };
      if (reduced()) { finish(); return; }
      // bırakınca süzülerek yavaşla
      let v = this.vel || 0.3;
      let last = performance.now();
      const glide = (now) => {
        const dt = Math.min(48, now - last);
        last = now;
        v *= 0.991 ** dt;
        if (v < 0.03 || this.holding) { finish(); return; }
        this.move(dir * v * dt);
        window.requestAnimationFrame(glide);
      };
      window.requestAnimationFrame(glide);
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-dabi-strip-nav]').forEach((vp) => {
      if (vp.dabiStrip) { vp.dabiStrip.place(); return; }
      vp.dabiStrip = new Strip(vp);
    });
  };
  // şeritler kayar, açılır, taşınır (raf, kategori şeridi, sekmeler): oklar her karede izler
  const follow = () => {
    if (strips.size) {
      hdr = headerState();
      strips.forEach((st) => st.place());
    }
    window.requestAnimationFrame(follow);
  };
  window.requestAnimationFrame(follow);
  const boot = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // ok üzerindeki hedefi ait olduğu şeride çevirir (oklar şeridin dışında, sayfa katmanında)
  const owner = (t) => { const n = t && t.closest ? t.closest('[data-dabi-snav]') : null; return (n && n.dabiOwner) || t; };
  window.DabiStrips = { init, owner };

  /* ---------- 3) Son bakılan ürünler: ürün sayfasında kaydet (koleksiyon sayfası gösterir) ---------- */
  const pm = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (pm && !window.location.search.includes('view=')) {
    try {
      const handle = decodeURIComponent(pm[1]);
      const list = JSON.parse(localStorage.getItem('dabi-recent') || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem('dabi-recent', JSON.stringify(list.slice(0, 16)));
    } catch (e) { /* depolama yok */ }
  }
  /* ---------- 4) Yüzen düğmeler (telefon): sağ alt kümede üst üste sırala, aşağı kaydırırken gizle ---------- */
  const dockMq = window.matchMedia('(max-width: 749px)');
  const dockItems = () => [...document.querySelectorAll('[data-dabi-dock]')];
  const layoutDock = () => {
    const items = dockItems();
    if (!dockMq.matches) { items.forEach((el) => el.style.removeProperty('--dabi-dock-slot')); return; }
    let acc = 0;
    items
      .filter((el) => getComputedStyle(el).display !== 'none')
      .sort((a, b) => (Number(a.dataset.dabiDock) || 0) - (Number(b.dataset.dabiDock) || 0))
      .forEach((el) => { el.style.setProperty('--dabi-dock-slot', `${acc}px`); acc += el.offsetHeight + 12; });
  };
  let dockLastY = window.scrollY;
  let dockIdle = null;
  const onDockScroll = () => {
    if (!dockMq.matches || !document.querySelector('[data-dabi-float-hide]')) return;
    const y = window.scrollY;
    const d = y - dockLastY;
    dockLastY = y;
    const root = document.documentElement;
    if (d > 6 && y > 160) root.classList.add('dabi-floats-hide');
    else if (d < -6 || y < 80) root.classList.remove('dabi-floats-hide');
    clearTimeout(dockIdle);
    dockIdle = setTimeout(() => root.classList.remove('dabi-floats-hide'), 1400);
  };
  const bootDock = () => { layoutDock(); window.addEventListener('load', layoutDock); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootDock); else bootDock();
  window.addEventListener('scroll', onDockScroll, { passive: true });
  window.addEventListener('resize', layoutDock, { passive: true });
  dockMq.addEventListener?.('change', layoutDock);
  document.addEventListener('shopify:section:load', layoutDock);
  document.addEventListener('shopify:section:unload', () => setTimeout(layoutDock, 50));
  /* ---------- 5) Özel sipariş baloncuğu: sayfada tek katman (body'de), rozetten büyüyerek açılır.
     Kart / şerit kırpmasından ve z-index yığınlarından bağımsız; büyüyen önizlemedeki kopya rozet de aynı katmanı kullanır.
     Dokunmatikte dokununca aç/kapat; PDP "Sipariş ver" → sepete ekle ---------- */
  // Paylaşılan: ürün bağlamında rozet (sections/dabi-mto-src) — kendi önizlemesi olan şeritler kullanır
  const mtoSrcCache = new Map();
  window.DabiMto = window.DabiMto || {};
  window.DabiMto.badgeFor = (url) => {
    const base = String(url || '').split(/[?#]/)[0];
    if (!/\/products\//.test(base)) return Promise.resolve(null);
    if (!mtoSrcCache.has(base)) {
      mtoSrcCache.set(base, fetch(`${base}?section_id=dabi-mto-src`, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => { const t = document.createElement('template'); t.innerHTML = html; return t.content.querySelector('[data-dabi-mto]'); })
        .catch(() => null));
    }
    return mtoSrcCache.get(base).then((el) => {
      if (!el) return null;
      const c = el.cloneNode(true);
      c.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      c.querySelectorAll('[aria-describedby]').forEach((n) => n.removeAttribute('aria-describedby'));
      return c;
    });
  };
  let mtoLayer = null;
  let mtoHost = null;
  let mtoTimer = 0;
  const hoverOk = () => window.matchMedia('(hover: hover)').matches;
  const mtoGetLayer = () => {
    if (mtoLayer) return mtoLayer;
    mtoLayer = document.createElement('div');
    mtoLayer.id = 'DabiMtoLayer';
    mtoLayer.className = 'dabi-mto-layer';
    mtoLayer.setAttribute('role', 'dialog');
    document.body.appendChild(mtoLayer);
    mtoLayer.addEventListener('pointerenter', () => clearTimeout(mtoTimer));
    mtoLayer.addEventListener('pointerleave', () => mtoScheduleClose());
    return mtoLayer;
  };
  let mtoHeld = null;
  const mtoHold = (host) => {
    const sec = host ? host.closest('.shopify-section') : null;
    if (mtoHeld && mtoHeld !== sec) mtoHeld.classList.remove('is-dabi-hold-mto');
    mtoHeld = sec;
    if (sec) sec.classList.add('is-dabi-hold-mto'); // şerit akıyorsa baloncuk açıkken durur
  };
  const mtoClose = () => {
    clearTimeout(mtoTimer);
    if (mtoLayer) mtoLayer.classList.remove('is-on');
    if (mtoHost) mtoHost.classList.remove('is-open');
    mtoHost = null;
    mtoHold(null);
  };
  const mtoScheduleClose = () => { clearTimeout(mtoTimer); mtoTimer = setTimeout(mtoClose, 180); };
  const mtoPlace = (layer, pill) => {
    const r = pill.getBoundingClientRect();
    const w = layer.offsetWidth || Math.min(320, window.innerWidth - 24);
    const h = layer.offsetHeight || 200;
    const x = Math.min(Math.max(12, r.left), window.innerWidth - w - 12);
    let y = r.top - 12 - h;
    // yapışkan başlık / çubukların altında kalmasın: yer yoksa rozetin altına açılır
    const hdr = document.querySelector('.shopify-section-group-header-group, .shopify-section-header');
    const minY = Math.max(8, (hdr ? hdr.getBoundingClientRect().bottom : 0) + 8);
    const below = y < minY;
    if (below) y = Math.min(r.bottom + 12, window.innerHeight - h - 8);
    layer.classList.toggle('is-below', below);
    const cx = r.left + r.width / 2;
    layer.style.setProperty('--mto-x', `${x}px`);
    layer.style.setProperty('--mto-y', `${y}px`);
    layer.style.setProperty('--mto-ax', `${Math.max(10, Math.min(w - 24, cx - x - 7))}px`);
    layer.style.setProperty('--mto-ox', `${Math.max(0, Math.min(w, cx - x))}px`); // büyüme rozetten başlar
    layer.style.setProperty('--mto-oy', below ? '0%' : '100%');
  };
  const mtoOpen = (host) => {
    const pill = host.querySelector('[data-dabi-mto-pill]');
    const src = host.querySelector('.dabi-mto__pop');
    if (!pill || !src) return;
    clearTimeout(mtoTimer);
    const layer = mtoGetLayer();
    if (mtoHost !== host) {
      if (mtoHost) mtoHost.classList.remove('is-open');
      layer.classList.remove('is-on');
      layer.innerHTML = src.innerHTML;
      mtoHost = host;
      host.classList.add('is-open');
      mtoHold(host);
      mtoPlace(layer, pill);
      setTimeout(() => { if (mtoHost === host) layer.classList.add('is-on'); }, 20); // önce yerleşsin, sonra büyüsün (rAF arka planda duraklar)
      return;
    }
    mtoPlace(layer, pill);
    layer.classList.add('is-on');
  };
  document.addEventListener('pointerenter', (e) => { const h = e.target.closest?.('[data-dabi-mto]'); if (h && hoverOk()) mtoOpen(h); }, true);
  document.addEventListener('pointerleave', (e) => { const h = e.target.closest?.('[data-dabi-mto]'); if (h && h === mtoHost) mtoScheduleClose(); }, true);
  // kaynak rozet kaybolduysa (önizleme kapandı) ya da imleç başka yerdeyse kapat
  document.addEventListener('pointerover', (e) => {
    if (!mtoHost) return;
    if (!document.contains(mtoHost)) { mtoClose(); return; }
    if (!e.target.closest?.('[data-dabi-mto], .dabi-mto-layer')) mtoScheduleClose();
  });
  document.addEventListener('focusin', (e) => {
    const h = e.target.closest?.('[data-dabi-mto]');
    if (h) mtoOpen(h);
    else if (mtoHost && !(mtoLayer && mtoLayer.contains(e.target))) mtoClose();
  });
  window.addEventListener('scroll', mtoClose, { passive: true });
  window.addEventListener('resize', mtoClose, { passive: true });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') mtoClose(); });
  // dokunmatik: rozete dokununca baloncuk aç / kapat. Yakalama aşamasında — rozet kabı tıklamanın
  // yayılmasını durduruyor (karta düşmesin diye); belge dinleyicisine hiç ulaşmaz, bağlantı ürüne giderdi.
  document.addEventListener('click', (e) => {
    const pill = e.target.closest && e.target.closest('[data-dabi-mto-pill]');
    if (!pill || hoverOk()) return;
    e.preventDefault();
    const host = pill.closest('[data-dabi-mto]');
    if (host === mtoHost) mtoClose(); else mtoOpen(host);
  }, true);
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-dabi-mto-pill]') && !hoverOk()) return;
    if (!e.target.closest('[data-dabi-mto], .dabi-mto-layer')) mtoClose();
    const buy = e.target.closest('[data-dabi-mto-buy]');
    if (buy) {
      const btn = document.querySelector('product-form [name="add"]');
      if (btn && !btn.disabled) { btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => btn.click(), 350); }
    }
  });
  // kartlardan #ozel-siparis ile gelince kartı vurgula
  if (window.location.hash === '#ozel-siparis') {
    const card = document.querySelector('[data-dabi-mto-card]');
    if (card) { card.classList.add('is-hint'); setTimeout(() => card.classList.remove('is-hint'), 2400); }
  }
})();
